import { Box, Slider, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { colors } from '../../../utils/theme';
import { AnalyteReport } from '../../../types';

interface AnalyteResultSliderProps {
  analyte: AnalyteReport;
}

export const LOW_RATIO = 33;
export const OPTIMAL_RATIO = 60;
export const HIGH_RATIO = 40;
export const ADJUSTED_HIGH_RATIO = 33;
export const OPTIMAL_MIN_RATIO = 33;

export const AnalyteResultSlider: React.FC<AnalyteResultSliderProps> = ({
  analyte,
}) => {
  const result = analyte.result_history[0].result;
  const optimalRange = analyte.optimal_max_value - analyte.optimal_min_value;
  const zeroBasedMin = analyte.optimal_min_value === 0;

  const preciseValue = useMemo(() => {
    if (result < analyte.optimal_min_value) {
      return {
        value: (result / analyte.optimal_min_value) * LOW_RATIO,
        label: 'LOW',
      };
    } else if (result <= analyte.optimal_max_value) {
      const ratio = (result - analyte.optimal_min_value) / optimalRange;
      return {
        value: zeroBasedMin
          ? ratio * OPTIMAL_RATIO
          : OPTIMAL_MIN_RATIO + ratio * OPTIMAL_MIN_RATIO,
        label: 'OPTIMAL',
      };
    } else {
      const ratio = (result - analyte.optimal_max_value) / optimalRange;
      return {
        value: zeroBasedMin
          ? OPTIMAL_RATIO + ratio * HIGH_RATIO
          : OPTIMAL_MIN_RATIO +
            ADJUSTED_HIGH_RATIO +
            ratio * ADJUSTED_HIGH_RATIO,
        label: 'HIGH',
      };
    }
  }, [
    result,
    analyte.optimal_min_value,
    analyte.optimal_max_value,
    optimalRange,
    zeroBasedMin,
  ]);

  const backgroundGradient = useMemo(() => {
    return analyte.optimal_min_value === 0
      ? `linear-gradient(90deg,${colors.primarys.GREEN},${colors.primarys.GREEN},${colors.primarys.GREEN},${colors.primarys.GREEN},${colors.primarys.YELLOW},orange,${colors.primarys.RED})`
      : `linear-gradient(90deg,${colors.primarys.RED},orange,${colors.primarys.YELLOW},${colors.primarys.GREEN},${colors.primarys.GREEN},${colors.primarys.GREEN},${colors.primarys.YELLOW},orange,${colors.primarys.RED})`;
  }, [analyte.optimal_min_value]);

  const marks = useMemo(() => {
    if (analyte.optimal_min_value === 0) {
      return [
        { value: 8, label: 'OPTIMAL' },
        { value: 93, label: 'HIGH' },
      ];
    } else {
      return [
        { value: 8, label: 'LOW' },
        { value: 50, label: 'OPTIMAL' },
        { value: 93, label: 'HIGH' },
      ];
    }
  }, [analyte.optimal_min_value]);

  return (
    <Box sx={{ width: '100%' }}>
      <Slider
        value={preciseValue.value}
        aria-labelledby="discrete-slider"
        valueLabelDisplay="auto"
        marks={marks.map((mark) => ({
          ...mark,
          label: (
            <Typography
              fontSize={12}
              variant={mark.label === preciseValue.label ? 'medium' : 'light'}
              sx={{
                opacity: mark.label === preciseValue.label ? 1 : 0.8,
                color: colors.greys.TEXT_BLACK,
              }}
            >
              {mark.label}
            </Typography>
          ),
        }))}
        disabled
        sx={{
          color: 'transparent',
          height: 20,
          borderRadius: 1.25,
          '& .MuiSlider-rail': {
            background: backgroundGradient,
            height: 20,
            opacity: 1,
          },
          '& .MuiSlider-track': {
            background: 'transparent',
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            '&:before': {
              content: '""',
              display: 'block',
              position: 'relative',
              borderRadius: '50%',
              backgroundColor: colors.greys.BASE_WHITE,
              height: 12,
              width: 12,
              zIndex: 1,
              border: `1px solid ${colors.greys.BRAND_BLACK}`,
            },
          },
          '& .MuiSlider-markLabel': {
            marginTop: 1,
          },
          '& .Mui-disabled': {
            color: 'transparent !important',
            cursor: 'none',
          },
          '& .MuiSlider-mark': {
            backgroundColor: 'transparent',
          },
          '& .MuiSlider-markActive': {
            backgroundColor: 'transparent',
            fontWeight: 300,
            color: colors.greys.TEXT_BLACK,
          },
        }}
      />
    </Box>
  );
};
